<template>
  <Modal
    class="tw-translate-y-[-50%] assessment-modal tw-max-w-[1040px]"
    :visible="showRef"
    border-radius="4px"
    @close="handleCloseModal"
  >
    <ModalHeader
      :class="`modal-header`"
      :title="
        getComponentUiText(
          'app-component-assessment-duplicate-assessment-modal-header-title-1',
        )
      "
      @close="handleCloseModal(true)"
    />
    <div
      class="tw-flex tw-flex-col tw-justify-center tw-items-start tw-gap-[40px] tw-self-stretch tw-p-[40px] tw-bg-[#FFFFFF]"
    >
      <div
        class="tw-flex tw-items-start tw-flex-col tw-gap-[15px] headingWrapper"
      >
        <span
          class="tw-text-[#211F58] tw-text-[22px] tw-not-italic tw-font-semibold tw-leading-[100%]"
        >
          {{
            getComponentUiText(
              "app-component-assessment-duplicate-assessment-modal-title-1",
            )
          }}
        </span>
        <span
          class="tw-text-[#231F20] tw-text-[15px] tw-not-italic tw-font-normal tw-leading-[145%]"
          >{{
            getComponentUiText(
              "app-component-assessment-duplicate-assessment-modal-description-1",
            )
          }}</span
        >
      </div>
      <div class="tw-w-full tw-h-full tw-flex tw-flex-col tw-gap-[25px]">
        <div
          class="tw-flex tw-flex-col tw-justify-between md:tw-items-start tw-gap-[20px] tw-self-stretch tw-rounded-[4px] tw-p-[10px] md:tw-p-[25px] main"
        >
          <div
            class="tw-w-full tw-flex md:tw-items-center tw-items-start tw-justify-between tw-gap-4 tw-flex-col md:tw-flex-row"
          >
            <div
              class="tw-text-[#211F58] tw-text-[15px] tw-not-italic tw-font-medium tw-leading-[15px] tw-min-w-[260px]"
            >
              {{
                getComponentUiText(
                  "app-component-assessment-duplicate-assessment-modal-field-name-1",
                )
              }}
              <span class="tw-ml-1 tw-text-[#F7553C]">*</span>
            </div>
            <div
              class="tw-flex tw-flex-col tw-w-full tw-max-w-[100%] md:tw-max-w-[630px] tw-relative"
            >
              <BFormInput
                v-model="formModel.name"
                type="text"
                :placeholder="
                  getComponentUiText(
                    'app-component-assessment-duplicate-assessment-modal-field-placeholder-name-1',
                  )
                "
                :class="`tw-border tw-text-[#231F20] tw-text-[15px] tw-h-[66px] tw-leading-none tw-px-[20px] tw-py-[18px] tw-rounded-md tw-border-solid tw-border-[#EFF0F1] tw-bg-[#FFFFFF] ${
                  $v?.$errors?.[0]?.$message
                    ? 'tw-border-[#f7553c] tw-bg-[#fee5e2] focus:!tw-ring-[0.2rem] focus:!tw-ring-[rgba(220,53,69,0.25)] focus:!tw-border-[#f7553c]'
                    : ''
                }`"
                class="autoFocus"
                @blur="$v.name.$touch()"
              />
              <span v-if="$v?.$errors?.[0]?.$message" class="invalid-message">{{
                $v?.$errors?.[0]?.$message
              }}</span>
            </div>
          </div>
          <div
            class="tw-w-full tw-flex tw-items-start tw-justify-between tw-gap-4 tw-flex-col md:tw-flex-row"
          >
            <div
              class="tw-text-[#211F58] tw-text-[15px] tw-not-italic tw-font-medium tw-leading-[15px] tw-min-w-[260px] tw-pt-[21px]"
            >
              {{
                getComponentUiText(
                  "app-component-assessment-duplicate-assessment-modal-field-description-1",
                )
              }}
            </div>
            <div
              class="tw-flex tw-flex-col tw-w-full tw-max-w-[100%] md:tw-max-w-[630px] tw-relative"
            >
              <textarea
                ref="autoTextarea"
                v-model="formModel.description"
                :placeholder="
                  getComponentUiText(
                    'app-component-assessment-duplicate-assessment-modal-field-placeholder-description-1',
                  )
                "
                :style="`outline-color : #EFF0F1`"
                class="auto-expand tw-min-h-[82px] tw-text-[15px] tw-font-normal tw-leading-none tw-px-5 tw-pt-[17.5px] tw-pb-[17px] tw-rounded-md tw-border-solid tw-border-[#EFF0F1]"
                @input="autoResize"
              ></textarea>
            </div>
          </div>
        </div>
      </div>
      <div class="tw-flex tw-flex-row tw-justify-end tw-items-center tw-w-full">
        <b-button
          variant="primary"
          class="tw-w-fit tw-h-[39px] tw-mr-[15px] tw-shadow-none tw-bg-[#fff] tw-text-[#387DFF] tw-px-[18px] tw-text-[15px] tw-font-medium tw-rounded-[27px] tw-border-2 tw-border-solid hover:!tw-border-[#0069d9]"
          type="submit"
          @click="handleCloseModal"
        >
          {{
            getComponentUiText(
              "app-component-assessment-duplicate-assessment-modal-button-cancel-1",
            )
          }}
        </b-button>
        <ButtonLoading
          :title="
            getComponentUiText(
              'app-component-assessment-duplicate-assessment-modal-button-duplicate-1',
            )
          "
          :is-loading="isLoading"
          @click-action="duplicateAssessment"
        />
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, PropType, reactive } from "vue";
import Modal from "@/components/common/modal.vue";
import ModalHeader from "@/components/common/modalHeader.vue";
import { getComponentUiText } from "@/utils/utilities";
import { IAssessmentDetail } from "@/interfaces/assessments/IAssessmentDetails";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import ButtonLoading from "../common/buttonLoading.vue";
import { showErrorAPI } from "@/services/apiRoutes";
import { Assessment } from "@/services/apiRoutes/routes";

const props = defineProps({
  show: {
    type: Boolean,
    required: false,
  },
  checklistAssessmentData: {
    type: Object as PropType<IAssessmentDetail>,
    required: true,
  },
  updateDataFucntion: {
    type: Function as PropType<(payload: any) => Promise<void>>,
    required: false,
    default: undefined,
  },
});

let showRef = ref(props.show);
const name = ref(props.checklistAssessmentData.checklistName);
const description: any = ref(
  props.checklistAssessmentData.checklistDescription,
);
const autoTextarea: any = ref(null);
const isLoading = ref(false);

const initialForm = { name, description };
// Form modal and validation.
const formModel = reactive({ ...initialForm });
const rules = {
  name: {
    required: helpers.withMessage(
      () => getComponentUiText("app-common-form-value-required-error-1"),
      required,
    ),
  },
};
const $v = useVuelidate(rules, formModel);

watch(
  () => props.show,
  (value) => {
    showRef.value = value;
  },
);

const autoResize = () => {
  if (autoTextarea.value) {
    autoTextarea.value.style.height = "auto"; // Reset height
    autoTextarea.value.style.height = autoTextarea.value.scrollHeight + "px"; // Set height based on content
  }
};

onMounted(() => {
  autoResize();
});

const emit = defineEmits(["update:show", "update:data"]);

const handleCloseModal = async (isClickIcon = false) => {
  if (!isClickIcon && (formModel.name || formModel.description)) return;

  showRef.value = false;
  emit("update:show", showRef.value);
};

const duplicateAssessment = async () => {
  try {
    isLoading.value = true;
    await $v.value.$validate();
    if ($v.value.$invalid) return;

    if (props.updateDataFucntion) {
      await props.updateDataFucntion({
        checklistName: formModel.name,
        checklistDescription: formModel.description,
      });
    } else {
      emit("update:data", {
        checklistName: formModel.name,
        checklistDescription: formModel.description,
      });
    }
    handleCloseModal(true);
  } catch (error: any) {
    showErrorAPI(error, Assessment.getCheckListDuplicate);
  } finally {
    isLoading.value = false;
  }
};
</script>

<style lang="scss" scoped>
.main {
  border: 1px solid #eff0f1;
  background: rgba(239, 240, 241, 0.5);
  transition: all 0.3s ease-in-out;
}

textarea::placeholder {
  color: #b7b7b7;
  font-size: 15px;
  font-family: Poppins;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}

input::placeholder {
  color: #b7b7b7;
  font-size: 15px;
  font-family: Poppins;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}

.auto-expand {
  width: 100%;
  overflow-y: hidden;
  resize: none;
  outline-color: #eff0f1;
  line-height: 18px;
  color: #231f20;
}

.invalid-message {
  font-size: 11px !important;
  font-family: Poppins !important;
  font-style: normal !important;
  font-weight: 500 !important;
  color: #f7553c !important;
  line-height: 130% !important;
  margin-top: 10px !important;
}
</style>
